"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import z from "zod";

import { Button } from "@/components/ui/button";
import { Field, FieldGroup, FieldLabel, FieldSet } from "@/components/ui/field";
import { Input } from "@/components/ui/input";

interface Step2Props {
  onNext: (code: string) => void;
  onResendCode: () => void;
  errorMessage?: string;
}

const authCodeSchema = z.object({
  authCode: z.string().min(6, "인증코드를 입력해 주세요"),
});

type FormValues = z.infer<typeof authCodeSchema>;

export default function Step2AuthCodeForm({
  onNext,
  onResendCode,
  errorMessage,
}: Readonly<Step2Props>) {
  const {
    register,
    formState: { errors },
    watch,
    trigger,
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(authCodeSchema),
    mode: "onChange",
  });

  const authCodeValue = watch("authCode");

  const handleSubmitStep = async () => {
    const isValid = await trigger("authCode");
    if (!isValid) {
      return;
    }

    onNext(authCodeValue);
  };

  const handleResendCode = () => {
    reset({ authCode: "" });
    onResendCode();
  };

  return (
    <FieldSet>
      <FieldGroup>
        <Field>
          <FieldLabel className="text-base">인증코드 입력</FieldLabel>
          <Input
            type="text"
            placeholder="메일로 받으신 코드를 입력해 주세요"
            aria-invalid={!!errors.authCode}
            {...register("authCode")}
            onChange={e => {
              const value = e.target.value;
              const filteredValue = value.replace(/[^a-zA-Z0-9]/g, "");
              e.target.value = filteredValue;
              register("authCode").onChange(e);
              trigger("authCode");
            }}
            className="h-11 text-base"
          />
          {errors.authCode && (
            <p className="text-error-600 pl-3 text-sm">{errors.authCode.message}</p>
          )}
          {errorMessage && !errors.authCode && (
            <p className="text-error-600 pl-3 text-sm">{errorMessage}</p>
          )}
        </Field>

        <Field className="pt-2">
          <Button
            type="button"
            onClick={handleSubmitStep}
            disabled={!authCodeValue || !!errors.authCode}
            className="text-md h-11"
          >
            확인
          </Button>
        </Field>

        <div className="flex items-center justify-between">
          <p className="text-sm">이메일을 받지 못하셨나요?</p>
          <Button variant="link" size="sm" onClick={handleResendCode}>
            인증코드 재발송
          </Button>
        </div>
      </FieldGroup>
    </FieldSet>
  );
}
